/**
 * LFO (tremolo) effect plugin.
 *
 * A classic tremolo: the audio signal passes through a `Tone.Gain`
 * whose `.gain` AudioParam is modulated by a free-running
 * `Tone.LFO`. Routes in → gain-node → out; the LFO is purely control-
 * rate and never touches the audio path directly.
 *
 * Params mirror the DeepSub's built-in LFO so users form one mental
 * model for both surfaces: `rate` (Hz), `depth` (0-1 dip amount),
 * `shape` (sine / triangle / square / sawtooth), `phase` (0-360° so
 * the waveform start offset is consistent across retriggers of the
 * host bus).
 *
 * Why not re-use Tone's built-in `Tone.Tremolo`? Tone.Tremolo
 * sine-locks the shape, hardcodes the min/max range, and buries its
 * LFO behind a synced-to-transport default that fights our free-rate
 * use case. A hand-rolled LFO + Gain gives us the exposed shape knob
 * + the deterministic phase reset the UX wants.
 */

import { Gain as ToneGain, LFO as ToneLFO } from 'tone'
import type { ToneOscillatorType } from 'tone'
import { registerEffect } from '@/audio/plugins/registry'
import type { EffectPlugin, PluginHandle } from '@/audio/plugins/types'
import { registerToneContext } from './registerContext'

const PLUGIN_ID = 'tone-lfo-tremolo'

const LFO_SHAPES: readonly ToneOscillatorType[] = [
  'sine',
  'triangle',
  'square',
  'sawtooth',
]

const SHAPE_OPTIONS: ReadonlyArray<[number, string]> = [
  [0, 'Sine'],
  [1, 'Triangle'],
  [2, 'Square'],
  [3, 'Sawtooth'],
]

let registered = false

export function registerLfoTremoloEffect(): void {
  if (registered) return
  registered = true
  registerEffect(lfoTremoloPlugin)
}

function shapeFromIndex(value: number): ToneOscillatorType {
  const i = Math.max(0, Math.min(LFO_SHAPES.length - 1, Math.round(value)))
  return LFO_SHAPES[i]
}

export const lfoTremoloPlugin: EffectPlugin = {
  id: PLUGIN_ID,
  label: 'LFO Tremolo',
  category: 'modulation',
  defaultPreset: {
    rate: 4,
    depth: 0.5,
    shape: 0,
    phase: 0,
  },
  paramSchema: [
    {
      id: 'rate',
      label: 'Rate',
      kind: 'slider',
      min: 0.05,
      max: 20,
      step: 0.05,
      default: 4,
      help: 'LFO frequency in Hz. Sub-audio range only.',
      macro: true,
    },
    {
      id: 'depth',
      label: 'Depth',
      kind: 'slider',
      min: 0,
      max: 1,
      step: 0.01,
      default: 0.5,
      help: 'How deep the gain dips. 0 = bypass, 1 = full gate.',
      macro: true,
    },
    {
      id: 'shape',
      label: 'Shape',
      kind: 'select',
      default: 0,
      options: SHAPE_OPTIONS,
    },
    {
      id: 'phase',
      label: 'Phase',
      kind: 'slider',
      min: 0,
      max: 360,
      step: 1,
      default: 0,
      help: 'Start-phase offset (degrees). Useful for dotted rhythm feels.',
    },
  ],
  createNode(ctx, preset = {}): PluginHandle {
    registerToneContext(ctx)

    const rate = typeof preset.rate === 'number' ? preset.rate : 4
    const depth = typeof preset.depth === 'number' ? preset.depth : 0.5
    const shapeIdx = typeof preset.shape === 'number' ? preset.shape : 0
    const phase = typeof preset.phase === 'number' ? preset.phase : 0

    // Base gain must be zero — Tone.Signal sums its base value with
    // every connected source, so a `gain: 1` base plus the LFO's
    // [1-depth, 1] output would clip far above unity. Setting base
    // to 0 makes the LFO's scaled output THE gain value.
    const modGain = new ToneGain({ gain: 0 })
    const lfo = new ToneLFO({
      frequency: rate,
      type: shapeFromIndex(shapeIdx),
      min: Math.max(0, 1 - depth),
      max: 1,
      phase,
    }).start()
    lfo.connect(modGain.gain)

    // Bus I/O. `modGain` is both the input node (signal feeds it) and
    // the output node (gain-modulated signal flows out). Wrapping in
    // stable Gain pass-throughs lets the bus splice our handle in
    // without leaking the internal modulation node's connection state.
    const input = ctx.createGain()
    const output = ctx.createGain()
    input.connect(modGain.input as unknown as AudioNode)
    ;(modGain.output as unknown as AudioNode).connect(output)

    return {
      input,
      output,
      setParam(name, value) {
        if (name === 'rate') {
          lfo.frequency.value = Math.max(0.01, value)
        } else if (name === 'depth') {
          // Update the LFO's min without re-creating it. Depth 0
          // flattens min→1 so the gain doesn't move.
          const d = Math.max(0, Math.min(1, value))
          lfo.min = Math.max(0, 1 - d)
          lfo.max = 1
        } else if (name === 'shape') {
          lfo.type = shapeFromIndex(value)
        } else if (name === 'phase') {
          lfo.phase = ((value % 360) + 360) % 360
        }
      },
      dispose() {
        try {
          input.disconnect()
          output.disconnect()
        } catch {
          /* already */
        }
        try {
          lfo.stop()
          lfo.dispose()
        } catch {
          /* already */
        }
        try {
          modGain.dispose()
        } catch {
          /* already */
        }
      },
    }
  },
}
